import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BackIcon } from "./icons";

/**
 * BackLink is the way back from a page: a chevron and the name of where it
 * goes, in a pill that lights up under a pointer.
 *
 * A drawn chevron rather than a "←" typed into the label. The character came
 * from whichever font rendered the text, sat low on the baseline, and had to be
 * written into every translation pointing the right way. The icon is centred
 * on the label and flips with the interface's direction by itself.
 *
 * Pulled into the margin by its own padding, so the chevron lines up with the
 * page's text edge rather than sitting indented from it.
 */
export default function BackLink({
  to,
  onClick,
  testID,
  children,
}: {
  /** Where it goes. Without it, `onClick` decides (e.g. history back). */
  to?: string;
  onClick?: () => void;
  testID?: string;
  children: ReactNode;
}) {
  const className =
    "group -ms-2 inline-flex items-center gap-1 rounded-full py-1 ps-1.5 pe-3 text-sm text-muted transition-colors hover:bg-sunk hover:text-ink";
  const content = (
    <>
      <BackIcon className="size-4 transition-transform group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5" />
      {children}
    </>
  );
  return to ? (
    <Link to={to} data-testid={testID} className={className}>
      {content}
    </Link>
  ) : (
    <button type="button" onClick={onClick} data-testid={testID} className={className}>
      {content}
    </button>
  );
}
